import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, FileText, Send, Eye, Trash2, Printer, Receipt, Download, Mail, Bell, Check } from 'lucide-react';
import { formatCurrency, formatNumber } from '@/lib/utils';
import { toast } from 'sonner';

export default function FacturenPage() {
  const { api, user } = useAuth();
  const { t, i18n } = useTranslation();
  
  // Logo URL helper
  const logoUrl = user?.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${user.logo_url}` : null;
  
  const BTW_OPTIONS = [
    { value: '21', label: '21%' },
    { value: '9', label: '9%' },
    { value: '0', label: '0%' },
    { value: '-1', label: i18n.language === 'en' ? 'Reverse charged' : 'Verlegd' },
    { value: '-2', label: i18n.language === 'en' ? 'Exempt' : 'Vrijgesteld' }
  ];

  const getBtwLabel = (value) => {
    const option = BTW_OPTIONS.find(o => o.value === String(value));
    return option ? option.label : `${value}%`;
  };
  
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [mileageEntries, setMileageEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  const [createForm, setCreateForm] = useState({
    client_id: '',
    selectedItems: {}, // { entry_id: { type: 'time'|'mileage', btw: '21' } }
    template: 'standaard',
    notities: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invoicesRes, clientsRes, timeRes, mileageRes] = await Promise.all([
        api.get('/invoices'),
        api.get('/clients'),
        api.get('/time-entries'),
        api.get('/mileage-entries')
      ]);
      setInvoices(invoicesRes.data);
      setClients(clientsRes.data);
      setTimeEntries(timeRes.data.filter(e => !e.gefactureerd && e.factureerbaar));
      setMileageEntries(mileageRes.data.filter(e => !e.gefactureerd));
    } catch (err) {
      toast.error('Kon gegevens niet laden');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setShowViewDialog(false);
    setSelectedInvoice(null);
    setCreateForm({
      client_id: '',
      selectedItems: {},
      template: 'standaard',
      notities: ''
    });
    setShowCreateDialog(true);
  };

  const handleClientChange = async (clientId) => {
    // Update client_id and reset selected items
    setCreateForm(prev => ({ ...prev, client_id: clientId, selectedItems: {} }));
    
    // Fetch last used template for this client
    if (clientId) {
      try {
        const res = await api.get(`/clients/${clientId}/last-template`);
        if (res.data?.template) {
          setCreateForm(prev => ({ ...prev, template: res.data.template }));
        }
      } catch (err) {
        // If error, keep default template
        console.log('Using default template');
      }
    }
  };

  const toggleItem = (entryId, type, entry) => {
    setCreateForm(prev => {
      const newItems = { ...prev.selectedItems };
      if (newItems[entryId]) {
        delete newItems[entryId];
      } else {
        newItems[entryId] = { 
          type, 
          btw: '21',
          bedrag: entry.totaal
        };
      }
      return { ...prev, selectedItems: newItems };
    });
  };

  const updateItemBtw = (entryId, btw) => {
    setCreateForm(prev => ({
      ...prev,
      selectedItems: {
        ...prev.selectedItems,
        [entryId]: { ...prev.selectedItems[entryId], btw }
      }
    }));
  };

  // Calculate live preview totals
  const previewTotals = useMemo(() => {
    const items = Object.entries(createForm.selectedItems);
    if (items.length === 0) return { subtotaal: 0, btwDetails: {}, btwTotaal: 0, totaal: 0 };

    let subtotaal = 0;
    const btwDetails = {};

    items.forEach(([entryId, item]) => {
      const entry = item.type === 'time' 
        ? timeEntries.find(e => e.entry_id === entryId)
        : mileageEntries.find(e => e.entry_id === entryId);
      
      if (entry) {
        subtotaal += entry.totaal;
        const btw = item.btw;
        const btwValue = parseFloat(btw);
        
        // Calculate BTW amount (negative values = 0% actual)
        const btwAmount = btwValue < 0 ? 0 : entry.totaal * (btwValue / 100);
        
        if (!btwDetails[btw]) {
          btwDetails[btw] = { bedrag: 0, basis: 0 };
        }
        btwDetails[btw].bedrag += btwAmount;
        btwDetails[btw].basis += entry.totaal;
      }
    });

    const btwTotaal = Object.values(btwDetails).reduce((sum, d) => sum + d.bedrag, 0);
    
    return {
      subtotaal: Math.round(subtotaal * 100) / 100,
      btwDetails,
      btwTotaal: Math.round(btwTotaal * 100) / 100,
      totaal: Math.round((subtotaal + btwTotaal) * 100) / 100
    };
  }, [createForm.selectedItems, timeEntries, mileageEntries]);

  const createInvoice = async () => {
    if (!createForm.client_id) {
      toast.error(t('timer.noClientSelected'));
      return;
    }
    const selectedItems = Object.entries(createForm.selectedItems);
    if (selectedItems.length === 0) {
      toast.error(t('invoices.noEntriesSelected'));
      return;
    }

    try {
      const time_entry_ids = selectedItems.filter(([_, item]) => item.type === 'time').map(([id]) => id);
      const mileage_entry_ids = selectedItems.filter(([_, item]) => item.type === 'mileage').map(([id]) => id);
      
      // Build line_items with BTW info
      const line_items = selectedItems.map(([entry_id, item]) => ({
        entry_id,
        entry_type: item.type,
        btw_percentage: parseFloat(item.btw)
      }));

      await api.post('/invoices', {
        client_id: createForm.client_id,
        time_entry_ids,
        mileage_entry_ids,
        line_items,
        template: createForm.template,
        notities: createForm.notities
      });
      
      toast.success(t('invoices.invoiceSaved'));
      setShowCreateDialog(false);
      setCreateForm({
        client_id: '',
        selectedItems: {},
        template: 'standaard',
        notities: ''
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.somethingWentWrong'));
    }
  };

  const viewInvoice = async (invoice) => {
    setShowCreateDialog(false);
    try {
      const res = await api.get(`/invoices/${invoice.invoice_id}`);
      setSelectedInvoice(res.data);
      setShowViewDialog(true);
    } catch (err) {
      toast.error(t('errors.somethingWentWrong'));
    }
  };

  const updateStatus = async (invoiceId, status) => {
    try {
      await api.put(`/invoices/${invoiceId}/status?status=${status}`);
      toast.success(t('settings.saved'));
      fetchData();
      if (selectedInvoice?.invoice_id === invoiceId) {
        setSelectedInvoice(prev => ({ ...prev, status }));
      }
    } catch (err) {
      toast.error(t('errors.somethingWentWrong'));
    }
  };

  const deleteInvoice = async (invoice) => {
    if (!confirm(t('invoices.confirmDelete'))) return;

    try {
      await api.delete(`/invoices/${invoice.invoice_id}`);
      toast.success(t('invoices.invoiceDeleted'));
      fetchData();
    } catch (err) {
      toast.error(t('errors.somethingWentWrong'));
    }
  };

  const sendInvoice = async (invoice) => {
    try {
      const res = await api.post(`/invoices/${invoice.invoice_id}/send`);
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.somethingWentWrong'));
    }
  };

  const downloadPdf = async (invoice) => {
    try {
      toast.info(t('common.loading'));
      const response = await api.get(`/invoices/${invoice.invoice_id}/pdf`, {
        responseType: 'blob'
      });
      
      const prefix = i18n.language === 'en' ? 'Invoice' : 'Factuur';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${prefix}_${invoice.invoice_nummer.replace(/-/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(t('common.download') + '!');
    } catch (err) {
      toast.error(t('errors.somethingWentWrong'));
    }
  };

  const sendReminder = async (invoice) => {
    try {
      const res = await api.post(`/invoices/${invoice.invoice_id}/reminder`);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.somethingWentWrong'));
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      concept: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
      verzonden: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
      betaald: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
      gecrediteerd: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
    };
    const labels = {
      concept: t('invoices.draft'),
      verzonden: t('invoices.sent'),
      betaald: t('invoices.paid'),
      gecrediteerd: i18n.language === 'en' ? 'Credited' : 'Gecrediteerd'
    };
    return <Badge className={styles[status]}>{labels[status]}</Badge>;
  };

  const filteredTimeEntries = timeEntries.filter(e => e.client_id === createForm.client_id);
  const filteredMileageEntries = mileageEntries.filter(e => e.client_id === createForm.client_id);
  const selectedClient = clients.find(c => c.client_id === createForm.client_id);

  if (loading) {
    return <div className="animate-pulse space-y-4">
      <div className="h-12 bg-muted rounded-xl w-1/3" />
      <div className="h-64 bg-muted rounded-xl" />
    </div>;
  }

  return (
    <div className="space-y-6" data-testid="facturen-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-heading text-foreground">{t('invoices.title')}</h1>
          <p className="text-muted-foreground">{t('invoices.subtitle')}</p>
        </div>
        <Button 
          className="bg-cyan-500 hover:bg-cyan-600 btn-active"
          onClick={openCreateDialog}
          data-testid="create-invoice-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('invoices.createInvoice')}
        </Button>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          {invoices.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('invoices.invoiceNumber')}</TableHead>
                    <TableHead>{t('common.client')}</TableHead>
                    <TableHead>{t('invoices.template')}</TableHead>
                    <TableHead>{t('common.date')}</TableHead>
                    <TableHead>{t('invoices.dueDate')}</TableHead>
                    <TableHead className="text-right">{t('common.amount')}</TableHead>
                    <TableHead>{t('common.status')}</TableHead>
                    <TableHead className="w-32"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow key={invoice.invoice_id}>
                      <TableCell className="font-mono font-medium">{invoice.invoice_nummer}</TableCell>
                      <TableCell>{invoice.client_naam}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          invoice.template === 'modern' 
                            ? 'bg-slate-800 text-cyan-400' 
                            : invoice.template === 'klassiek'
                            ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300'
                            : 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/50 dark:text-cyan-300'
                        }`}>
                          {invoice.template === 'modern' ? t('invoices.templateModern') : 
                           invoice.template === 'klassiek' ? t('invoices.templateClassic') : 
                           t('invoices.templateStandard')}
                        </span>
                      </TableCell>
                      <TableCell>{invoice.datum}</TableCell>
                      <TableCell>{invoice.vervaldatum}</TableCell>
                      <TableCell className="text-right font-semibold">{formatCurrency(invoice.totaal)}</TableCell>
                      <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => viewInvoice(invoice)}
                            data-testid={`view-invoice-${invoice.invoice_id}`}
                            title={t('common.edit')}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => downloadPdf(invoice)}
                            data-testid={`download-pdf-${invoice.invoice_id}`}
                            title={t('invoices.downloadPdf')}
                            className="text-green-600"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          {invoice.status === 'concept' && (
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => sendInvoice(invoice)}
                              className="text-blue-600"
                              title={t('invoices.sendInvoice')}
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          )}
                          {invoice.status === 'verzonden' && (
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => sendReminder(invoice)}
                              className="text-amber-600"
                              title={t('invoices.sendReminder')}
                            >
                              <Bell className="w-4 h-4" />
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => deleteInvoice(invoice)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">{t('invoices.noInvoices')}</p>
              <Button onClick={openCreateDialog} className="bg-cyan-500 hover:bg-cyan-600">
                <Plus className="w-4 h-4 mr-2" />
                {t('invoices.createInvoice')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Invoice Dialog with Live Preview */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading">{t('invoices.createInvoice')}</DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 py-4">
              {/* Left side: Selection */}
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label>{t('common.client')} *</Label>
                  <Select 
                    value={createForm.client_id} 
                    onValueChange={handleClientChange}
                  >
                    <SelectTrigger data-testid="invoice-client-select">
                      <SelectValue placeholder={t('timer.selectClient')} />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map(c => (
                        <SelectItem key={c.client_id} value={c.client_id}>{c.naam}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Template Selector */}
                <div className="space-y-3">
                  <Label>{t('invoices.template')}</Label>
                  <p className="text-xs text-muted-foreground">{t('invoices.templateDescription')}</p>
                  <div className="grid grid-cols-3 gap-3">
                    {/* Standard Template */}
                    <button
                      type="button"
                      onClick={() => setCreateForm(prev => ({ ...prev, template: 'standaard' }))}
                      className={`relative p-3 rounded-lg border-2 transition-all text-left ${
                        createForm.template === 'standaard' 
                          ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-950/30' 
                          : 'border-border hover:border-cyan-300'
                      }`}
                      data-testid="template-standard"
                    >
                      {createForm.template === 'standaard' && (
                        <div className="absolute top-2 right-2 w-5 h-5 bg-cyan-500 rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                      <div className="space-y-2">
                        <div className="w-full h-16 bg-gradient-to-b from-cyan-100 to-white dark:from-cyan-900/30 dark:to-background rounded border border-cyan-200 dark:border-cyan-800 overflow-hidden">
                          <div className="h-4 bg-cyan-500 flex items-center px-2">
                            <div className="w-8 h-1 bg-white/60 rounded" />
                          </div>
                          <div className="p-1.5 space-y-1">
                            <div className="w-full h-1 bg-slate-200 dark:bg-slate-700 rounded" />
                            <div className="w-3/4 h-1 bg-slate-200 dark:bg-slate-700 rounded" />
                            <div className="w-1/2 h-1 bg-cyan-300 dark:bg-cyan-700 rounded" />
                          </div>
                        </div>
                        <p className="text-sm font-medium text-foreground">{t('invoices.templateStandard')}</p>
                      </div>
                    </button>

                    {/* Modern Template */}
                    <button
                      type="button"
                      onClick={() => setCreateForm(prev => ({ ...prev, template: 'modern' }))}
                      className={`relative p-3 rounded-lg border-2 transition-all text-left ${
                        createForm.template === 'modern' 
                          ? 'border-slate-800 dark:border-slate-300 bg-slate-50 dark:bg-slate-900' 
                          : 'border-border hover:border-slate-400'
                      }`}
                      data-testid="template-modern"
                    >
                      {createForm.template === 'modern' && (
                        <div className="absolute top-2 right-2 w-5 h-5 bg-slate-800 dark:bg-slate-300 rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-white dark:text-slate-800" />
                        </div>
                      )}
                      <div className="space-y-2">
                        <div className="w-full h-16 bg-white dark:bg-slate-900 rounded border border-slate-300 dark:border-slate-600 overflow-hidden">
                          <div className="h-4 bg-slate-800 dark:bg-slate-200 flex items-center justify-between px-2">
                            <div className="w-6 h-1 bg-white/60 dark:bg-slate-600 rounded" />
                            <div className="w-4 h-1 bg-cyan-400 rounded" />
                          </div>
                          <div className="p-1.5 space-y-1">
                            <div className="w-2/3 h-1 bg-slate-300 dark:bg-slate-600 rounded" />
                            <div className="w-full h-1 bg-slate-200 dark:bg-slate-700 rounded" />
                            <div className="w-1/3 h-1 bg-slate-800 dark:bg-slate-300 rounded" />
                          </div>
                        </div>
                        <p className="text-sm font-medium text-foreground">{t('invoices.templateModern')}</p>
                      </div>
                    </button>

                    {/* Classic Template */}
                    <button
                      type="button"
                      onClick={() => setCreateForm(prev => ({ ...prev, template: 'klassiek' }))}
                      className={`relative p-3 rounded-lg border-2 transition-all text-left ${
                        createForm.template === 'klassiek' 
                          ? 'border-amber-700 bg-amber-50 dark:bg-amber-950/30' 
                          : 'border-border hover:border-amber-400'
                      }`}
                      data-testid="template-classic"
                    >
                      {createForm.template === 'klassiek' && (
                        <div className="absolute top-2 right-2 w-5 h-5 bg-amber-700 rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                      <div className="space-y-2">
                        <div className="w-full h-16 bg-amber-50 dark:bg-amber-950/50 rounded border border-amber-200 overflow-hidden">
                          <div className="h-4 border-b-2 border-amber-700 dark:border-amber-600 flex items-center justify-center">
                            <div className="w-10 h-1.5 bg-amber-700 dark:bg-amber-600 rounded" />
                          </div>
                          <div className="p-1.5 space-y-1">
                            <div className="w-full h-1 bg-amber-200 dark:bg-amber-800 rounded" />
                            <div className="w-2/3 h-1 bg-amber-200 dark:bg-amber-800 rounded" />
                            <div className="flex justify-end">
                              <div className="w-1/3 h-1 bg-amber-700 dark:bg-amber-600 rounded" />
                            </div>
                          </div>
                        </div>
                        <p className="text-sm font-medium text-foreground">{t('invoices.templateClassic')}</p>
                      </div>
                    </button>
                  </div>
                </div>

                {createForm.client_id && (
                  <>
                    {/* Time Entries with BTW selection */}
                    <div className="space-y-2">
                      <Label>{t('registrations.timeEntries')}</Label>
                      {filteredTimeEntries.length > 0 ? (
                        <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                          {filteredTimeEntries.map(entry => {
                            const isSelected = !!createForm.selectedItems[entry.entry_id];
                            return (
                              <div key={entry.entry_id} className="p-3 hover:bg-muted">
                                <div className="flex items-center gap-3">
                                  <Checkbox
                                    checked={isSelected}
                                    onCheckedChange={() => toggleItem(entry.entry_id, 'time', entry)}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">{entry.datum} - {entry.beschrijving || (i18n.language === 'en' ? 'Work' : 'Werkzaamheden')}</p>
                                    <p className="text-xs text-muted-foreground">{formatNumber(entry.uren, 2)} {t('common.hours')} × {formatCurrency(entry.uurtarief)}</p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {isSelected && (
                                      <Select 
                                        value={createForm.selectedItems[entry.entry_id]?.btw || '21'}
                                        onValueChange={(v) => updateItemBtw(entry.entry_id, v)}
                                      >
                                        <SelectTrigger className="w-28 h-8 text-xs">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {BTW_OPTIONS.map(opt => (
                                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    )}
                                    <span className="font-semibold whitespace-nowrap">{formatCurrency(entry.totaal)}</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground p-3 border rounded-lg">{t('registrations.noEntries')}</p>
                      )}
                    </div>

                    {/* Mileage Entries with BTW selection */}
                    <div className="space-y-2">
                      <Label>{t('registrations.mileageEntries')}</Label>
                      {filteredMileageEntries.length > 0 ? (
                        <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                          {filteredMileageEntries.map(entry => {
                            const isSelected = !!createForm.selectedItems[entry.entry_id];
                            const routeText = entry.van_locatie && entry.naar_locatie 
                              ? `${entry.van_locatie} → ${entry.naar_locatie}`
                              : entry.van_locatie || entry.naar_locatie || (i18n.language === 'en' ? 'Trip' : 'Rit');
                            return (
                              <div key={entry.entry_id} className="p-3 hover:bg-muted">
                                <div className="flex items-center gap-3">
                                  <Checkbox
                                    checked={isSelected}
                                    onCheckedChange={() => toggleItem(entry.entry_id, 'mileage', entry)}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">{entry.datum} - {routeText}</p>
                                    <p className="text-xs text-muted-foreground">{formatNumber(entry.kilometers, 0)} km × {formatCurrency(entry.km_tarief)}</p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {isSelected && (
                                      <Select 
                                        value={createForm.selectedItems[entry.entry_id]?.btw || '21'}
                                        onValueChange={(v) => updateItemBtw(entry.entry_id, v)}
                                      >
                                        <SelectTrigger className="w-28 h-8 text-xs">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {BTW_OPTIONS.map(opt => (
                                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    )}
                                    <span className="font-semibold whitespace-nowrap">{formatCurrency(entry.totaal)}</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground p-3 border rounded-lg">Geen open ritregistraties</p>
                      )}
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <Label>Notities (optioneel)</Label>
                  <Textarea
                    value={createForm.notities}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, notities: e.target.value }))}
                    placeholder="Aanvullende informatie voor de factuur..."
                  />
                </div>
              </div>

              {/* Right side: Live Preview */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Receipt className="w-4 h-4" />
                  Live voorbeeld
                </div>
                
                {/* Template-based preview - ALWAYS light mode (print style) */}
                <div className={`p-6 text-sm max-h-[500px] overflow-y-auto rounded-lg border shadow-sm ${
                  createForm.template === 'modern' 
                    ? 'bg-white border-slate-300' 
                    : createForm.template === 'klassiek'
                    ? 'bg-amber-50 border-amber-200'
                    : 'bg-white border-slate-200'
                }`} style={{colorScheme: 'light'}}>
                  
                  {/* MODERN Template Preview */}
                  {createForm.template === 'modern' && (
                    <>
                      {/* Dark header bar */}
                      <div className="bg-slate-900 -m-6 mb-4 p-4 flex justify-between items-center">
                        <div className="flex items-center gap-3">
                          {logoUrl && <img src={logoUrl} alt="Logo" className="h-8 w-auto object-contain bg-white rounded p-1" />}
                          <span className="text-white font-bold">{user?.bedrijfsnaam || 'Urenbuddy'}</span>
                        </div>
                        <span className="text-cyan-400 font-bold text-xl">FACTUUR</span>
                      </div>
                      
                      {/* Invoice number with cyan underline */}
                      <div className="mb-4 pb-2 border-b-2 border-cyan-400">
                        <span className="text-xs text-slate-500">Factuurnummer</span>
                        <p className="font-bold text-slate-900">CONCEPT</p>
                      </div>
                      
                      {/* Client info */}
                      <div className="grid grid-cols-2 gap-4 mb-4 text-xs">
                        <div>
                          <p className="text-slate-500 uppercase text-[10px]">Van</p>
                          <p className="font-semibold text-slate-900">{user?.bedrijfsnaam || '—'}</p>
                          {user?.adres && <p className="text-slate-600">{user.adres}</p>}
                        </div>
                        <div>
                          <p className="text-slate-500 uppercase text-[10px]">Factuur aan</p>
                          <p className="font-semibold text-slate-900">{selectedClient?.naam || '—'}</p>
                          {selectedClient?.adres && <p className="text-slate-600">{selectedClient.adres}</p>}
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* KLASSIEK Template Preview */}
                  {createForm.template === 'klassiek' && (
                    <>
                      {/* Centered header with logo */}
                      <div className="text-center mb-4">
                        {logoUrl && <img src={logoUrl} alt="Logo" className="h-12 w-auto object-contain mx-auto mb-2" />}
                        <h2 className="text-lg font-bold text-amber-800" style={{fontFamily: 'Georgia, serif'}}>{user?.bedrijfsnaam || 'Urenbuddy'}</h2>
                        <div className="flex items-center justify-center gap-2 my-2">
                          <div className="h-0.5 w-12 bg-amber-600"></div>
                          <div className="h-0.5 w-12 bg-amber-600"></div>
                        </div>
                        <h3 className="text-amber-700 font-bold" style={{fontFamily: 'Georgia, serif'}}>FACTUUR</h3>
                        <p className="text-xs text-amber-600">CONCEPT</p>
                      </div>
                      
                      {/* Two column info */}
                      <div className="flex justify-between mb-4 text-xs" style={{fontFamily: 'Georgia, serif'}}>
                        <div>
                          {user?.adres && <p className="text-amber-700">{user.adres}</p>}
                          {user?.postcode && <p className="text-amber-700">{user.postcode} {user.plaats}</p>}
                        </div>
                        <div className="text-right">
                          <p className="text-amber-600">Factuur aan:</p>
                          <p className="font-semibold text-amber-800">{selectedClient?.naam || '—'}</p>
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* STANDAARD Template Preview */}
                  {createForm.template === 'standaard' && (
                    <>
                      {/* Header with logo */}
                      <div className="flex justify-between mb-6">
                        <div className="flex items-start gap-3">
                          {logoUrl && <img src={logoUrl} alt="Logo" className="h-12 w-auto object-contain" />}
                          <div>
                            <h2 className="text-lg font-bold text-slate-900">{user?.bedrijfsnaam || 'Urenbuddy'}</h2>
                            {user?.adres && <p className="text-xs text-slate-500">{user.adres}</p>}
                            {user?.postcode && user?.plaats && <p className="text-xs text-slate-500">{user.postcode} {user.plaats}</p>}
                          </div>
                        </div>
                        <div className="text-right">
                          <h3 className="text-xl font-bold text-cyan-500">FACTUUR</h3>
                          <p className="text-xs text-slate-500 mt-1">CONCEPT</p>
                        </div>
                      </div>

                      {/* Client */}
                      <div className="mb-6">
                        <p className="text-xs text-slate-500">Factuur aan:</p>
                        <p className="font-semibold text-slate-900">{selectedClient?.naam || '—'}</p>
                      </div>
                    </>
                  )}

                  {/* Lines - styled per template (always light mode) */}
                  {Object.keys(createForm.selectedItems).length > 0 ? (
                    <>
                      <table className="w-full mb-4 text-xs">
                        <thead>
                          <tr className={`border-b ${
                            createForm.template === 'modern' 
                              ? 'bg-slate-900 text-white' 
                              : createForm.template === 'klassiek'
                              ? 'bg-amber-100 text-amber-800 border-amber-600'
                              : 'bg-slate-100 text-slate-800'
                          }`}>
                            <th className="text-left py-2 px-2 font-semibold">Omschrijving</th>
                            <th className="text-right py-2 px-2 font-semibold">BTW</th>
                            <th className="text-right py-2 px-2 font-semibold">Bedrag</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(createForm.selectedItems).map(([entryId, item]) => {
                            const entry = item.type === 'time' 
                              ? timeEntries.find(e => e.entry_id === entryId)
                              : mileageEntries.find(e => e.entry_id === entryId);
                            if (!entry) return null;
                            
                            const desc = item.type === 'time' 
                              ? entry.beschrijving || 'Werkzaamheden'
                              : entry.van_locatie && entry.naar_locatie 
                                ? `Km: ${entry.van_locatie} - ${entry.naar_locatie}`
                                : 'Kilometers';
                            
                            return (
                              <tr key={entryId} className={`border-b ${
                                createForm.template === 'klassiek' ? 'border-amber-200' : 'border-slate-100'
                              }`}>
                                <td className="py-2 px-2">
                                  <p className="font-medium text-slate-900">{desc}</p>
                                  <p className="text-slate-500">{entry.datum}</p>
                                </td>
                                <td className="text-right py-2 px-2 text-slate-700">{getBtwLabel(item.btw)}</td>
                                <td className="text-right py-2 px-2 font-semibold text-slate-900">{formatCurrency(entry.totaal)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>

                      {/* Totals - styled per template (always light mode) */}
                      <div className="flex justify-end">
                        <div className="w-48">
                          <div className="flex justify-between py-1 text-xs">
                            <span className="text-slate-500">Subtotaal</span>
                            <span className="text-slate-900">{formatCurrency(previewTotals.subtotaal)}</span>
                          </div>
                          {Object.entries(previewTotals.btwDetails).map(([btw, details]) => (
                            <div key={btw} className="flex justify-between py-1 text-xs">
                              <span className="text-slate-500">BTW {getBtwLabel(btw)}</span>
                              <span className="text-slate-900">{formatCurrency(details.bedrag)}</span>
                            </div>
                          ))}
                          <div className={`flex justify-between py-2 border-t-2 font-bold ${
                            createForm.template === 'modern' 
                              ? 'border-cyan-400' 
                              : createForm.template === 'klassiek'
                              ? 'border-amber-600'
                              : 'border-slate-900'
                          }`}>
                            <span className="text-slate-900">Totaal</span>
                            <span className={
                              createForm.template === 'modern' 
                                ? 'text-cyan-600' 
                                : createForm.template === 'klassiek'
                                ? 'text-amber-700'
                                : 'text-cyan-500'
                            }>{formatCurrency(previewTotals.totaal)}</span>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className={`text-center py-8 ${
                      createForm.template === 'klassiek' ? 'text-amber-600' : 'text-slate-400'
                    }`}>
                      <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-xs">Selecteer registraties om een voorbeeld te zien</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Annuleren</Button>
            <Button 
              className="bg-cyan-500 hover:bg-cyan-600" 
              onClick={createInvoice} 
              data-testid="invoice-create-btn"
              disabled={Object.keys(createForm.selectedItems).length === 0}
            >
              Factuur aanmaken
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Invoice Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center justify-between">
              <span>Factuur {selectedInvoice?.invoice_nummer}</span>
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                  selectedInvoice?.template === 'modern' 
                    ? 'bg-slate-800 text-cyan-400' 
                    : selectedInvoice?.template === 'klassiek'
                    ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300'
                    : 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/50 dark:text-cyan-300'
                }`}>
                  {selectedInvoice?.template === 'modern' ? t('invoices.templateModern') : 
                   selectedInvoice?.template === 'klassiek' ? t('invoices.templateClassic') : 
                   t('invoices.templateStandard')}
                </span>
                {selectedInvoice && getStatusBadge(selectedInvoice.status)}
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className={`p-8 my-4 rounded-lg border shadow-sm ${
              selectedInvoice.template === 'modern' 
                ? 'bg-white border-slate-300' 
                : selectedInvoice.template === 'klassiek'
                ? 'bg-amber-50 border-amber-200'
                : 'bg-white border-slate-200'
            }`} style={{colorScheme: 'light'}}>
              
              {/* ===== MODERN TEMPLATE ===== */}
              {selectedInvoice.template === 'modern' && (
                <>
                  {/* Dark header bar with logo */}
                  <div className="bg-slate-900 -m-8 mb-6 p-6 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      {logoUrl && <img src={logoUrl} alt="Logo" className="h-10 w-auto object-contain bg-white rounded p-1" />}
                      <span className="text-white font-bold text-lg">{user?.bedrijfsnaam || 'Urenbuddy'}</span>
                    </div>
                    <span className="text-cyan-400 font-bold text-2xl">FACTUUR</span>
                  </div>
                  
                  {/* Invoice number with cyan underline */}
                  <div className="mb-6 pb-2 border-b-2 border-cyan-400">
                    <span className="text-xs text-slate-500">Factuurnummer</span>
                    <p className="font-bold text-lg text-slate-900">{selectedInvoice.invoice_nummer}</p>
                  </div>
                  
                  {/* Three column info */}
                  <div className="grid grid-cols-3 gap-4 mb-6 text-sm">
                    <div>
                      <p className="text-slate-500 uppercase text-xs mb-1">Van</p>
                      <p className="font-semibold text-slate-900">{user?.bedrijfsnaam || '—'}</p>
                      {user?.adres && <p className="text-slate-600 text-xs">{user.adres}</p>}
                      {user?.postcode && <p className="text-slate-600 text-xs">{user.postcode} {user.plaats}</p>}
                      {user?.btw_nummer && <p className="text-slate-600 text-xs">BTW: {user.btw_nummer}</p>}
                    </div>
                    <div>
                      <p className="text-slate-500 uppercase text-xs mb-1">Factuur aan</p>
                      <p className="font-semibold text-slate-900">{selectedInvoice.client_naam}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 uppercase text-xs mb-1">Datum</p>
                      <p className="text-sm">Factuurdatum: <strong>{selectedInvoice.datum}</strong></p>
                      <p className="text-sm">Vervaldatum: <strong>{selectedInvoice.vervaldatum}</strong></p>
                    </div>
                  </div>
                  
                  {/* Lines table - modern style */}
                  <table className="w-full mb-6">
                    <thead>
                      <tr className="bg-slate-900 text-white">
                        <th className="text-left py-3 px-3 text-sm font-semibold">Omschrijving</th>
                        <th className="text-right py-3 px-3 text-sm font-semibold">Aantal</th>
                        <th className="text-right py-3 px-3 text-sm font-semibold">Tarief</th>
                        <th className="text-right py-3 px-3 text-sm font-semibold">BTW</th>
                        <th className="text-right py-3 px-3 text-sm font-semibold">Bedrag</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedInvoice.regels?.map((regel, i) => (
                        <tr key={i} className="border-b border-slate-200">
                          <td className="py-3 px-3 text-sm">
                            <p className="font-medium">{regel.beschrijving}</p>
                            <p className="text-xs text-slate-500">{regel.datum}</p>
                          </td>
                          <td className="text-right py-3 px-3 text-sm font-mono">
                            {regel.type === 'uren' ? `${formatNumber(regel.aantal, 2)} uur` : `${formatNumber(regel.aantal, 0)} km`}
                          </td>
                          <td className="text-right py-3 px-3 text-sm">{formatCurrency(regel.tarief)}</td>
                          <td className="text-right py-3 px-3 text-sm">{getBtwLabel(regel.btw_percentage || 21)}</td>
                          <td className="text-right py-3 px-3 text-sm font-semibold">{formatCurrency(regel.bedrag)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {/* Totals - modern style */}
                  <div className="flex justify-end">
                    <div className="w-64">
                      <div className="flex justify-between py-2 text-sm">
                        <span className="text-slate-500">Subtotaal</span>
                        <span>{formatCurrency(selectedInvoice.subtotaal)}</span>
                      </div>
                      {selectedInvoice.btw_details ? (
                        Object.entries(selectedInvoice.btw_details).map(([btw, amount]) => (
                          <div key={btw} className="flex justify-between py-2 text-sm">
                            <span className="text-slate-500">BTW {getBtwLabel(btw)}</span>
                            <span>{formatCurrency(amount)}</span>
                          </div>
                        ))
                      ) : (
                        <div className="flex justify-between py-2 text-sm">
                          <span className="text-slate-500">BTW ({selectedInvoice.btw_percentage || 21}%)</span>
                          <span>{formatCurrency(selectedInvoice.btw_bedrag)}</span>
                        </div>
                      )}
                      <div className="flex justify-between py-3 border-t-2 border-cyan-400 font-bold text-lg">
                        <span>Totaal</span>
                        <span className="text-cyan-600">{formatCurrency(selectedInvoice.totaal)}</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
              
              {/* ===== KLASSIEK TEMPLATE ===== */}
              {selectedInvoice.template === 'klassiek' && (
                <>
                  {/* Centered header with logo */}
                  <div className="text-center mb-6">
                    {logoUrl && <img src={logoUrl} alt="Logo" className="h-16 w-auto object-contain mx-auto mb-3" />}
                    <h2 className="text-xl font-bold text-amber-800 dark:text-amber-400" style={{fontFamily: 'Georgia, serif'}}>{user?.bedrijfsnaam || 'Urenbuddy'}</h2>
                    <div className="flex items-center justify-center gap-4 my-3">
                      <div className="h-0.5 w-16 bg-amber-600"></div>
                      <div className="h-0.5 w-16 bg-amber-600"></div>
                    </div>
                    <h3 className="text-xl text-amber-700 dark:text-amber-500 font-bold" style={{fontFamily: 'Georgia, serif'}}>FACTUUR</h3>
                    <p className="text-amber-600 font-mono">{selectedInvoice.invoice_nummer}</p>
                  </div>
                  
                  {/* Two column info */}
                  <div className="flex justify-between mb-6" style={{fontFamily: 'Georgia, serif'}}>
                    <div className="text-sm">
                      {user?.adres && <p className="text-amber-700">{user.adres}</p>}
                      {user?.postcode && <p className="text-amber-700">{user.postcode} {user.plaats}</p>}
                      {user?.btw_nummer && <p className="text-amber-700">BTW: {user.btw_nummer}</p>}
                      {user?.kvk_nummer && <p className="text-amber-700">KvK: {user.kvk_nummer}</p>}
                    </div>
                    <div className="text-right text-sm">
                      <p className="text-amber-600">Factuur aan:</p>
                      <p className="font-semibold text-amber-800">{selectedInvoice.client_naam}</p>
                    </div>
                  </div>
                  
                  {/* Dates row */}
                  <div className="flex justify-between mb-6 text-sm" style={{fontFamily: 'Georgia, serif'}}>
                    <p className="text-amber-700">Factuurdatum: {selectedInvoice.datum}</p>
                    <p className="text-amber-700">Vervaldatum: {selectedInvoice.vervaldatum}</p>
                  </div>
                  
                  {/* Lines table - classic style */}
                  <table className="w-full mb-6" style={{fontFamily: 'Georgia, serif'}}>
                    <thead>
                      <tr className="bg-amber-100 border-2 border-amber-600">
                        <th className="text-left py-3 px-3 text-sm font-semibold text-amber-800">Omschrijving</th>
                        <th className="text-right py-3 px-3 text-sm font-semibold text-amber-800">Aantal</th>
                        <th className="text-right py-3 px-3 text-sm font-semibold text-amber-800">Tarief</th>
                        <th className="text-right py-3 px-3 text-sm font-semibold text-amber-800">BTW</th>
                        <th className="text-right py-3 px-3 text-sm font-semibold text-amber-800">Bedrag</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedInvoice.regels?.map((regel, i) => (
                        <tr key={i} className="border-b border-amber-200">
                          <td className="py-3 px-3 text-sm">
                            <p className="font-medium text-amber-900">{regel.beschrijving}</p>
                            <p className="text-xs text-amber-600">{regel.datum}</p>
                          </td>
                          <td className="text-right py-3 px-3 text-sm font-mono text-amber-900">
                            {regel.type === 'uren' ? `${formatNumber(regel.aantal, 2)} uur` : `${formatNumber(regel.aantal, 0)} km`}
                          </td>
                          <td className="text-right py-3 px-3 text-sm text-amber-900">{formatCurrency(regel.tarief)}</td>
                          <td className="text-right py-3 px-3 text-sm text-amber-900">{getBtwLabel(regel.btw_percentage || 21)}</td>
                          <td className="text-right py-3 px-3 text-sm font-semibold text-amber-900">{formatCurrency(regel.bedrag)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {/* Totals - classic style */}
                  <div className="flex justify-end">
                    <div className="w-64" style={{fontFamily: 'Georgia, serif'}}>
                      <div className="flex justify-between py-2 text-sm">
                        <span className="text-amber-600">Subtotaal</span>
                        <span className="text-amber-900">{formatCurrency(selectedInvoice.subtotaal)}</span>
                      </div>
                      {selectedInvoice.btw_details ? (
                        Object.entries(selectedInvoice.btw_details).map(([btw, amount]) => (
                          <div key={btw} className="flex justify-between py-2 text-sm">
                            <span className="text-amber-600">BTW {getBtwLabel(btw)}</span>
                            <span className="text-amber-900">{formatCurrency(amount)}</span>
                          </div>
                        ))
                      ) : (
                        <div className="flex justify-between py-2 text-sm">
                          <span className="text-amber-600">BTW ({selectedInvoice.btw_percentage || 21}%)</span>
                          <span className="text-amber-900">{formatCurrency(selectedInvoice.btw_bedrag)}</span>
                        </div>
                      )}
                      <div className="flex justify-between py-3 border-t-2 border-b-2 border-amber-600 font-bold text-lg">
                        <span className="text-amber-800">Totaal</span>
                        <span className="text-amber-700">{formatCurrency(selectedInvoice.totaal)}</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
              
              {/* ===== STANDAARD TEMPLATE ===== */}
              {(selectedInvoice.template === 'standaard' || !selectedInvoice.template) && (
                <>
                  {/* Invoice Header with logo */}
                  <div className="flex justify-between mb-8">
                    <div className="flex items-start gap-4">
                      {logoUrl && <img src={logoUrl} alt="Logo" className="h-16 w-auto object-contain" />}
                      <div>
                        <h2 className="text-xl font-bold font-heading">{user?.bedrijfsnaam || 'Urenbuddy'}</h2>
                        {user?.adres && <p className="text-sm text-muted-foreground">{user.adres}</p>}
                        {user?.postcode && user?.plaats && <p className="text-sm text-muted-foreground">{user.postcode} {user.plaats}</p>}
                        {user?.btw_nummer && <p className="text-sm text-muted-foreground">BTW: {user.btw_nummer}</p>}
                        {user?.kvk_nummer && <p className="text-sm text-muted-foreground">KvK: {user.kvk_nummer}</p>}
                      </div>
                    </div>
                    <div className="text-right">
                      <h3 className="text-2xl font-bold text-cyan-500">FACTUUR</h3>
                      <p className="font-mono text-lg mt-2">{selectedInvoice.invoice_nummer}</p>
                    </div>
                  </div>

                  {/* Client & Dates */}
                  <div className="grid grid-cols-2 gap-8 mb-8">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Factuur aan:</p>
                      <p className="font-semibold">{selectedInvoice.client_naam}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm"><span className="text-muted-foreground">Factuurdatum:</span> {selectedInvoice.datum}</p>
                      <p className="text-sm"><span className="text-muted-foreground">Vervaldatum:</span> {selectedInvoice.vervaldatum}</p>
                    </div>
                  </div>

                  {/* Invoice Lines */}
                  <table className="w-full mb-8">
                    <thead>
                      <tr className="border-b-2 border-slate-200 bg-slate-100">
                        <th className="text-left py-3 px-2 text-sm font-semibold">Omschrijving</th>
                        <th className="text-right py-3 px-2 text-sm font-semibold">Aantal</th>
                        <th className="text-right py-3 px-2 text-sm font-semibold">Tarief</th>
                        <th className="text-right py-3 px-2 text-sm font-semibold">BTW</th>
                        <th className="text-right py-3 px-2 text-sm font-semibold">Bedrag</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedInvoice.regels?.map((regel, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="py-3 px-2 text-sm">
                            <p className="font-medium">{regel.beschrijving}</p>
                            <p className="text-xs text-muted-foreground">{regel.datum}</p>
                          </td>
                          <td className="text-right py-3 px-2 text-sm font-mono">
                            {regel.type === 'uren' ? `${formatNumber(regel.aantal, 2)} uur` : `${formatNumber(regel.aantal, 0)} km`}
                          </td>
                          <td className="text-right py-3 px-2 text-sm">{formatCurrency(regel.tarief)}</td>
                          <td className="text-right py-3 px-2 text-sm">{getBtwLabel(regel.btw_percentage || 21)}</td>
                          <td className="text-right py-3 px-2 text-sm font-semibold">{formatCurrency(regel.bedrag)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Totals */}
                  <div className="flex justify-end">
                    <div className="w-64">
                      <div className="flex justify-between py-2">
                        <span className="text-muted-foreground">Subtotaal</span>
                        <span>{formatCurrency(selectedInvoice.subtotaal)}</span>
                      </div>
                      {selectedInvoice.btw_details ? (
                        Object.entries(selectedInvoice.btw_details).map(([btw, amount]) => (
                          <div key={btw} className="flex justify-between py-2">
                            <span className="text-muted-foreground">BTW {getBtwLabel(btw)}</span>
                            <span>{formatCurrency(amount)}</span>
                          </div>
                        ))
                      ) : (
                        <div className="flex justify-between py-2">
                          <span className="text-muted-foreground">BTW ({selectedInvoice.btw_percentage || 21}%)</span>
                          <span>{formatCurrency(selectedInvoice.btw_bedrag)}</span>
                        </div>
                      )}
                      <div className="flex justify-between py-3 border-t-2 border-slate-900 font-bold text-lg">
                        <span>Totaal</span>
                        <span className="text-cyan-500">{formatCurrency(selectedInvoice.totaal)}</span>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Notes - all templates */}
              {selectedInvoice.notities && (
                <div className={`mt-8 pt-4 border-t ${
                  selectedInvoice.template === 'klassiek' ? 'border-amber-300' : 'border-slate-200'
                }`}>
                  <p className={`text-sm ${
                    selectedInvoice.template === 'klassiek' ? 'text-amber-700 italic' : 'text-muted-foreground'
                  }`} style={selectedInvoice.template === 'klassiek' ? {fontFamily: 'Georgia, serif'} : {}}>
                    {selectedInvoice.notities}
                  </p>
                </div>
              )}

              {/* Payment Info - all templates */}
              <div className={`mt-8 pt-4 border-t text-sm ${
                selectedInvoice.template === 'klassiek' 
                  ? 'border-amber-300 text-amber-700' 
                  : selectedInvoice.template === 'modern'
                  ? 'border-slate-200 text-slate-500'
                  : 'border-slate-200 text-muted-foreground'
              }`} style={selectedInvoice.template === 'klassiek' ? {fontFamily: 'Georgia, serif'} : {}}>
                <p>Betaling graag binnen 30 dagen na factuurdatum.</p>
                {user?.iban && <p>IBAN: {user.iban}</p>}
              </div>

              {/* Branding for free plan */}
              {user?.subscription_plan === 'free' && (
                <div className={`mt-8 pt-4 border-t text-center ${
                  selectedInvoice.template === 'klassiek' ? 'border-amber-300' : 'border-slate-200'
                }`}>
                  <p className={`text-xs ${
                    selectedInvoice.template === 'klassiek' ? 'text-amber-600' : 'text-muted-foreground'
                  }`}>Gemaakt met Urenbuddy.nl</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <div className="flex gap-2 flex-wrap">
              {selectedInvoice?.status === 'concept' && (
                <Button variant="outline" onClick={() => updateStatus(selectedInvoice.invoice_id, 'verzonden')}>
                  <Send className="w-4 h-4 mr-2" />
                  Markeer als verzonden
                </Button>
              )}
              {selectedInvoice?.status === 'concept' && (
                <Button variant="outline" onClick={() => sendInvoice(selectedInvoice)} className="text-blue-600">
                  <Mail className="w-4 h-4 mr-2" />
                  E-mail naar klant
                </Button>
              )}
              {selectedInvoice?.status === 'verzonden' && (
                <Button variant="outline" onClick={() => updateStatus(selectedInvoice.invoice_id, 'betaald')} className="text-green-600">
                  Markeer als betaald
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => selectedInvoice && downloadPdf(selectedInvoice)} className="text-green-600">
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
              <Button variant="outline" onClick={() => window.print()}>
                <Printer className="w-4 h-4 mr-2" />
                Printen
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
